<template>
  <!-- ══════════ SELECT COMPANY / ENTITY DIALOG ══════════
       Step 1 : Pick a company  (multi-company users)
       Step 2 : Pick an entity  (multi-entity companies)
       Loading: Branded transition screen while workspace loads
  ═══════════════════════════════════════════════════════════ -->
  <v-dialog :value="visible" max-width="480" persistent @input="$emit('update:visible', $event)">
    <v-card class="switch-card">

      <!-- ── Header (hidden during loading) ── -->
      <transition name="fade">
        <div v-if="!loading" class="dialog-header">
          <div class="header-left">
            <v-btn
              v-if="step === 2 && is_multi_company"
              icon small class="back-btn mr-2"
              @click="go_back"
            >
              <v-icon size="18">mdi-arrow-left</v-icon>
            </v-btn>
            <div class="header-text">
              <div class="header-eyebrow">{{ step === 1 ? 'WORKSPACE' : 'ENTITY' }}</div>
              <h2 class="header-title">{{ step === 1 ? 'Select your company' : 'Select an entity' }}</h2>
              <span class="header-subtitle" v-if="step === 2 && selected_company_label">
                {{ selected_company_label }}
              </span>
            </div>
          </div>
          <v-btn icon class="close-btn" @click="cancel">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </transition>

      <!-- ══ LOADING TRANSITION SCREEN ══ -->
      <transition name="slide-up" mode="out-in">
        <div v-if="loading" class="loading-screen" key="loading">

          <!-- Pulsing avatar -->
          <div class="ls-avatar-ring">
            <div class="ls-avatar" :class="switching_type === 'entity' ? 'ls-avatar--entity' : 'ls-avatar--company'">
              {{ getInitials(switching_label) }}
            </div>
          </div>

          <!-- Company / entity name -->
          <div class="ls-name">{{ switching_label }}</div>
          <div class="ls-sub" v-if="switching_type === 'entity' && selected_company_label">
            {{ selected_company_label }}
          </div>

          <!-- Cycling status messages -->
          <transition name="msg-fade" mode="out-in">
            <div class="ls-msg" :key="loading_msg">{{ loading_msg }}</div>
          </transition>

          <!-- Animated dot track -->
          <div class="ls-dots">
            <span class="ls-dot"></span>
            <span class="ls-dot"></span>
            <span class="ls-dot"></span>
          </div>

          <!-- Shimmer progress bar -->
          <div class="ls-bar">
            <div class="ls-bar-fill"></div>
          </div>

        </div>

        <!-- ══ SELECTION BODY ══ -->
        <div v-else class="dialog-body" key="body">

          <!-- Search box (shared by both steps) -->
          <div class="search-box">
            <v-icon size="16" color="#9A96A8" class="search-icon">mdi-magnify</v-icon>
            <input
              v-model="search"
              class="search-input"
              type="text"
              :placeholder="step === 1 ? 'Search company…' : 'Search entity…'"
            />
            <v-icon v-if="search" size="16" color="#9A96A8" class="search-clear" @click.stop="search = ''">mdi-close</v-icon>
          </div>

          <!-- Step 1: Company list -->
          <template v-if="step === 1">
            <div class="tile-list">
              <div
                v-for="company in paged_company_list"
                :key="company.sub_domain"
                class="row-tile"
                @click="select_company(company)"
              >
                <div class="row-avatar row-avatar--company">{{ getInitials(company.label) }}</div>
                <div class="row-info">
                  <div class="row-title">{{ company.label }}</div>
                  <div class="row-meta">{{ company.entity_count }} {{ company.entity_count === 1 ? 'entity' : 'entities' }}</div>
                </div>
                <v-icon size="16" color="#9A96A8" class="row-arrow">mdi-chevron-right</v-icon>
              </div>
              <div v-if="filtered_company_list.length === 0" class="empty-state">No companies match "{{ search }}"</div>
            </div>

            <div v-if="company_total_pages > 1" class="pager">
              <v-btn icon x-small :disabled="company_page === 1" @click="company_page--">
                <v-icon size="16">mdi-chevron-left</v-icon>
              </v-btn>
              <span class="pager-label">{{ company_page }} / {{ company_total_pages }}</span>
              <v-btn icon x-small :disabled="company_page === company_total_pages" @click="company_page++">
                <v-icon size="16">mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Step 2: Entity list -->
          <template v-else>
            <div class="tile-list">
              <div
                v-for="entity in paged_entity_list"
                :key="entity.entity_id"
                class="row-tile"
                @click="select_entity(entity)"
              >
                <div class="row-avatar row-avatar--entity">{{ getInitials(entity.entity_name) }}</div>
                <div class="row-info">
                  <div class="row-title">{{ entity.entity_name }}</div>
                  <div class="row-meta" v-if="entity.finyear && entity.finyear.trim()">FY: {{ entity.finyear }}</div>
                </div>
                <v-icon size="16" color="#9A96A8" class="row-arrow">mdi-chevron-right</v-icon>
              </div>
              <div v-if="filtered_entity_list.length === 0" class="empty-state">No entities match "{{ search }}"</div>
            </div>

            <div v-if="entity_total_pages > 1" class="pager">
              <v-btn icon x-small :disabled="entity_page === 1" @click="entity_page--">
                <v-icon size="16">mdi-chevron-left</v-icon>
              </v-btn>
              <span class="pager-label">{{ entity_page }} / {{ entity_total_pages }}</span>
              <v-btn icon x-small :disabled="entity_page === entity_total_pages" @click="entity_page++">
                <v-icon size="16">mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </template>

        </div>
      </transition>

    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "SwitchCompanyDialog",

  props: {
    visible:  { type: Boolean, default: false },
    entities: { type: Array,   default: () => [] },
    loading:  { type: Boolean, default: false }
  },

  data() {
    return {
      step: 1,
      selected_domain:        null,
      selected_company_label: null,
      filtered_entities:      [],
      search:                 '',
      company_page:           1,
      entity_page:            1,
      page_size:              6,
      // Loading screen state
      switching_label: '',
      switching_type:  'company',   // 'company' | 'entity'
      loading_msg:     'Connecting…',
      loading_msg_idx: 0,
      loading_msg_timer: null,
      loading_msgs: [
        'Connecting to your workspace…',
        'Loading your data…',
        'Preparing modules…',
        'Almost there…',
      ],
    };
  },

  computed: {
    company_list() {
      const seen = {};
      const list = [];
      for (const e of this.entities) {
        if (!seen[e.sub_domain]) {
          seen[e.sub_domain] = true;
          list.push({
            sub_domain:   e.sub_domain,
            label:        e.entity_name_actual || e.sub_domain,
            entity_count: this.entities.filter(x => x.sub_domain === e.sub_domain).length,
          });
        }
      }
      return list;
    },

    is_multi_company() {
      return this.company_list.length > 1;
    },

    filtered_company_list() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.company_list;
      return this.company_list.filter(c => c.label.toLowerCase().includes(q));
    },

    company_total_pages() {
      return Math.max(1, Math.ceil(this.filtered_company_list.length / this.page_size));
    },

    paged_company_list() {
      const start = (this.company_page - 1) * this.page_size;
      return this.filtered_company_list.slice(start, start + this.page_size);
    },

    filtered_entity_list() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.filtered_entities;
      return this.filtered_entities.filter(e => (e.entity_name || '').toLowerCase().includes(q));
    },

    entity_total_pages() {
      return Math.max(1, Math.ceil(this.filtered_entity_list.length / this.page_size));
    },

    paged_entity_list() {
      const start = (this.entity_page - 1) * this.page_size;
      return this.filtered_entity_list.slice(start, start + this.page_size);
    },
  },

  watch: {
    visible(val) {
      if (val) this.init();
    },

    search() {
      this.company_page = 1;
      this.entity_page   = 1;
    },

    loading(val) {
      if (val) {
        this.loading_msg_idx = 0;
        this.loading_msg     = this.loading_msgs[0];
        this.loading_msg_timer = setInterval(() => {
          this.loading_msg_idx = (this.loading_msg_idx + 1) % this.loading_msgs.length;
          this.loading_msg     = this.loading_msgs[this.loading_msg_idx];
        }, 1600);
      } else {
        clearInterval(this.loading_msg_timer);
        this.loading_msg_timer = null;
      }
    },
  },

  beforeDestroy() {
    clearInterval(this.loading_msg_timer);
  },

  methods: {
    init() {
      this.step                  = 1;
      this.selected_domain       = null;
      this.selected_company_label = null;
      this.filtered_entities     = [];
      this.search                = '';
      this.company_page          = 1;
      this.entity_page           = 1;

      if (!this.entities.length) return;

      if (!this.is_multi_company) {
        const company = this.company_list[0];
        this.selected_domain       = company.sub_domain;
        this.selected_company_label = company.label;
        this.filtered_entities     = this.entities.filter(e => e.sub_domain === company.sub_domain);

        if (this.filtered_entities.length === 1) {
          this.$nextTick(() => this.$emit("confirm", this.filtered_entities[0]));
          return;
        }
        this.step = 2;
      }
    },

    select_company(company) {
      this.selected_domain        = company.sub_domain;
      this.selected_company_label = company.label;
      this.filtered_entities      = this.entities.filter(e => e.sub_domain === company.sub_domain);
      this.search                 = '';
      this.entity_page            = 1;

      if (this.filtered_entities.length === 1) {
        this.switching_label = company.label;
        this.switching_type  = 'company';
        this.$emit("confirm", this.filtered_entities[0]);
        return;
      }
      this.step = 2;
    },

    select_entity(entity) {
      this.switching_label = entity.entity_name;
      this.switching_type  = 'entity';
      this.$emit("confirm", entity);
    },

    go_back() {
      this.step                  = 1;
      this.selected_domain       = null;
      this.selected_company_label = null;
      this.filtered_entities     = [];
      this.search                = '';
      this.company_page          = 1;
    },

    cancel() {
      this.$emit("update:visible", false);
      this.$emit("cancel");
    },

    getInitials(name) {
      if (!name) return '?';
      const words = name.trim().split(/\s+/).filter(Boolean);
      if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
      return (words[0][0] + words[1][0]).toUpperCase();
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap');

* { font-family: "Plus Jakarta Sans", system-ui, sans-serif !important; }

/* ── Card ── */
.switch-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 20px 60px rgba(30, 30, 47, 0.2), 0 4px 16px rgba(30, 30, 47, 0.08) !important;
}

/* ── Header ── */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 18px 20px 14px;
  background: #ffffff;
  border-bottom: 1px solid #F0EEF5;
}

.header-left {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.back-btn { color: #6B7280 !important; margin-top: 4px !important; }
.back-btn:hover { color: #3B4CC0 !important; }

.header-text { display: flex; flex-direction: column; gap: 2px; }

.header-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #9A96A8;
  text-transform: uppercase;
  line-height: 1;
}

.header-title {
  font-family: "DM Serif Display", Georgia, serif !important;
  font-size: 18px;
  font-weight: 400;
  color: #1E1E2F;
  margin: 0;
  line-height: 1.25;
}

.header-subtitle { font-size: 12px; color: #6B7280; font-weight: 500; margin-top: 2px; }

.close-btn { color: #9A96A8 !important; }
.close-btn:hover { color: #1E1E2F !important; background: rgba(30, 30, 47, 0.04) !important; }

/* ── Body ── */
.dialog-body {
  padding: 12px 16px 16px;
  max-height: 400px;
  overflow-y: auto;
  background: #ffffff;
}

.dialog-body::-webkit-scrollbar { width: 6px; }
.dialog-body::-webkit-scrollbar-track { background: transparent; }
.dialog-body::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 3px; }
.dialog-body::-webkit-scrollbar-thumb:hover { background: #9A96A8; }

/* ── Search box ── */
.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 9px;
  background: #F7F8FC;
  border: 1px solid #E5E7EB;
}

.search-icon { flex-shrink: 0; }

.search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #1E1E2F;
}

.search-input::placeholder { color: #9A96A8; font-weight: 400; }

.search-clear { flex-shrink: 0; cursor: pointer; }

.empty-state {
  padding: 24px 8px;
  text-align: center;
  font-size: 13px;
  color: #9A96A8;
}

/* ── Pager ── */
.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #F0EEF5;
}

.pager-label { font-size: 12px; font-weight: 600; color: #6B7280; min-width: 40px; text-align: center; }

/* ── Tile list ── */
.tile-list { display: flex; flex-direction: column; gap: 4px; }

.row-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 10px;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  border: 1px solid transparent;
}

.row-tile:hover { background: #F7F8FC; border-color: #E5E7EB; }
.row-tile:hover .row-arrow { color: #3B4CC0 !important; transform: translateX(2px); }

.row-avatar {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

.row-avatar--company { background: linear-gradient(135deg, #3B4CC0 0%, #6366F1 100%); box-shadow: 0 2px 8px rgba(59,76,192,.25); }
.row-avatar--entity  { background: linear-gradient(135deg, #E8726F 0%, #F59E92 100%); box-shadow: 0 2px 8px rgba(232,114,111,.25); }

.row-info { flex: 1; min-width: 0; }
.row-title { font-size: 13px; font-weight: 600; color: #1E1E2F; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row-meta  { font-size: 11px; color: #6B7280; font-weight: 500; margin-top: 1px; }
.row-arrow { transition: transform 0.15s ease, color 0.15s ease; flex-shrink: 0; }


/* ════════════════════════════════════════
   LOADING TRANSITION SCREEN
════════════════════════════════════════ */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 52px 40px 48px;
  min-height: 320px;
  background: #ffffff;
  text-align: center;
}

/* Pulsing ring around avatar */
.ls-avatar-ring {
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
}

.ls-avatar-ring::before,
.ls-avatar-ring::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 20px;
  border: 2px solid rgba(59, 76, 192, 0.20);
  animation: ring-pulse 2s ease-in-out infinite;
}

.ls-avatar-ring::after {
  inset: -12px;
  border-color: rgba(59, 76, 192, 0.08);
  animation-delay: 0.4s;
}

.ls-avatar {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.5px;
}

.ls-avatar--company {
  background: linear-gradient(135deg, #3B4CC0 0%, #6366F1 100%);
  box-shadow: 0 6px 24px rgba(59, 76, 192, 0.35);
}

.ls-avatar--entity {
  background: linear-gradient(135deg, #E8726F 0%, #F59E92 100%);
  box-shadow: 0 6px 24px rgba(232, 114, 111, 0.35);
}

.ls-name {
  font-family: "DM Serif Display", Georgia, serif !important;
  font-size: 20px;
  font-weight: 400;
  color: #1E1E2F;
  margin-bottom: 4px;
  line-height: 1.3;
}

.ls-sub {
  font-size: 12px;
  color: #9A96A8;
  font-weight: 500;
  margin-bottom: 4px;
}

/* Cycling status message */
.ls-msg {
  font-size: 13px;
  color: #6B7280;
  font-weight: 500;
  margin: 20px 0 16px;
  min-height: 20px;
}

/* Animated dots */
.ls-dots {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
}

.ls-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3B4CC0;
  animation: dot-bounce 1.2s ease-in-out infinite;
}

.ls-dot:nth-child(2) { animation-delay: 0.2s; }
.ls-dot:nth-child(3) { animation-delay: 0.4s; }

/* Progress bar */
.ls-bar {
  width: 200px;
  height: 3px;
  border-radius: 2px;
  background: #EEF0F6;
  overflow: hidden;
}

.ls-bar-fill {
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, #3B4CC0 0%, #6366F1 50%, #3B4CC0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.6s ease-in-out infinite;
}


/* ════════════════════════════════════════
   TRANSITIONS
════════════════════════════════════════ */

/* Header fade */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* Body ↔ loading screen */
.slide-up-enter-active { transition: all 0.28s cubic-bezier(.22,.68,0,1.2); }
.slide-up-leave-active { transition: all 0.18s ease; }
.slide-up-enter { opacity: 0; transform: translateY(14px); }
.slide-up-leave-to { opacity: 0; transform: translateY(-8px); }

/* Cycling status message */
.msg-fade-enter-active, .msg-fade-leave-active { transition: all 0.3s ease; }
.msg-fade-enter { opacity: 0; transform: translateY(6px); }
.msg-fade-leave-to { opacity: 0; transform: translateY(-6px); }


/* ════════════════════════════════════════
   KEYFRAMES
════════════════════════════════════════ */
@keyframes ring-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(1.06); }
}

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
  40%            { transform: scale(1.1); opacity: 1; }
}

@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
